import { ReactElement, useEffect, useMemo, useState } from 'react';

import curry from 'lodash/curry';

import Modal from '~/ui/components/common/Modal';

import Forms from '../HealthDetails/Forms';
import DetailsBlock from './DetailsBlock';
import TeamConfirmationModal from './TeamConfirmationModal';

import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import { IClientTeam } from '~/services/api/clientDetails/types';
import { IClientDetailedV3 } from '~/services/api/client/types';
import { IDetailsInfo, IDetails, ITypeDetails } from './types';
import groupByStatus from '~/utils/grouping/groupByStatus';

import styles from './TeamsDetails.module.scss';

interface IProps {
  client: IClientDetailedV3;
  clinicId?: string;
  actTeamId?: string;
}

const TeamsDetails = ({ client, clinicId, actTeamId }: IProps): ReactElement => {
  const [modalTitle, setModalTitle] = useState<string | null>(null);
  const [formType, setFormType] = useState<ITypeDetails | null>(null);
  const [details, setDetails] = useState<IDetails | null>(null);
  const [detailsInfo, setDetailsInfo] = useState<IDetailsInfo | null>(null);

  const { current, actTeams } = useStoreState(state => state.clientDetails);

  const { setCurrent } = useStoreActions(actions => actions.clientDetails);

  const { isLocalAdmin, isProgramAssistant } = useRole();

  const team: IClientTeam | undefined = useMemo(
    () => actTeams.find(item => item.id === details?.id),
    [actTeams, details?.id],
  );

  useEffect(() => {
    setCurrent(team || null);
  }, [team, setCurrent]);

  const onAdd = (title: string, type: ITypeDetails) => {
    setModalTitle(title);
    setFormType(type);
  };

  const onEdit = curry(
    (
      type: ITypeDetails,
      healthDetailsType: string,
      removeHealthDetailsName: boolean,
      id: number,
      healthDetailsName: string,
    ) => {
      let title = `Edit ${healthDetailsName} ${healthDetailsType}`;

      if (removeHealthDetailsName) {
        title = title.replace(/“[\w\s]*”/gi, '');
      }

      setModalTitle(title);
      setDetails({ type, id });
      setFormType(type);
    },
  );

  const requestInfo = { clientId: String(client.id), clinicId, teamId: actTeamId };

  const showSecondSubmit = !Object.keys(details || {}).length;

  const isClientActive = !client.isArchived;

  const { active, archived } = groupByStatus(actTeams);

  return (
    <div className={styles.wrapper}>
      <DetailsBlock
        onAdd={() => onAdd('Add Team', 'team')}
        onEdit={onEdit('team', 'Team', false)}
        title="Assigned Teams"
        buttonText={isClientActive && (isLocalAdmin || isProgramAssistant) && 'Add Team'}
        showEdit={isClientActive}
        active={active}
        archived={archived}
        setDetailsInfo={rest => setDetailsInfo({ typeDetails: 'team', ...rest })}
        showArchivedButton
        noActions={(!isLocalAdmin && !isProgramAssistant) || !isClientActive}
      />
      <div className={styles.width}>
        {(modalTitle || (details && details.id)) && (
          <Modal
            className={styles.modal}
            open
            onClose={() => {
              setModalTitle(null);
              setDetails(null);
              setCurrent(null);
            }}
          >
            <div className={styles.modalWrapper}>
              <h3 className={styles.modalTitle}>{modalTitle}</h3>
              <Forms
                formType={formType}
                setModalTitle={setModalTitle}
                setDetails={setDetails}
                requestInfo={requestInfo}
                details={details}
                showSecondSubmit={showSecondSubmit}
                current={current}
                primaryDiagnosis={null}
              />
            </div>
          </Modal>
        )}
        {detailsInfo && (
          <TeamConfirmationModal
            detailsInfo={detailsInfo}
            onClose={() => setDetailsInfo(null)}
            setDetailsInfo={setDetailsInfo}
            clientId={String(client?.id)}
            clinicId={clinicId}
            teamId={actTeamId}
          />
        )}
      </div>
    </div>
  );
};

export default TeamsDetails;
